import * as Dialog from '@radix-ui/react-dialog';
import { Ban, RefreshCw, Wrench, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { FailedProvisioningJob } from '@control-center/shared';
import {
  formatBRDateTime,
  ignoreJobRequestSchema,
  resolveManualRequestSchema,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { extractErrorMessage } from '../../lib/errors';
import { useIgnoreJob, useReprocessJob, useResolveJobManual } from '../../hooks/use-provisioning';

interface FailedJobCardProps {
  job: FailedProvisioningJob;
  canManage: boolean;
}

const ACTION_LABEL: Record<string, string> = { grant: 'Conceder acesso', revoke: 'Revogar acesso' };

export function FailedJobCard({ job, canManage }: FailedJobCardProps): JSX.Element {
  const [resolveOpen, setResolveOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);

  const reprocessMut = useReprocessJob();
  const resolveMut = useResolveJobManual();
  const ignoreMut = useIgnoreJob();

  const saasLabel = job.saas?.name ?? 'SaaS desconhecido';
  const actionLabel = ACTION_LABEL[job.action] ?? job.action;

  async function handleReprocess() {
    try {
      await reprocessMut.mutateAsync(job.id);
      toast.success(`Reprocessando: ${saasLabel}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao reprocessar o job.'));
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-[8px] border border-bad/30 bg-panel-2 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-ink">{saasLabel}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-2">
            <span
              className={cn(
                'rounded-[5px] border px-1.5 py-[1px]',
                job.action === 'revoke' ? 'border-bad/40 text-bad' : 'border-line text-muted',
              )}
            >
              {actionLabel}
            </span>
            {job.saas?.slug && <code className="text-muted-3">{job.saas.slug}</code>}
            <span>· {job.attemptsCount} tentativa(s)</span>
            {job.attemptedAt && <span>· última {formatBRDateTime(job.attemptedAt)}</span>}
          </div>
          {job.errorMessage && (
            <div className="mt-1 max-w-prose break-words text-[11px] text-bad">
              {job.errorMessage}
            </div>
          )}
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReprocess}
            disabled={reprocessMut.isPending}
            className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 text-[12px] text-ink hover:bg-panel disabled:opacity-50"
          >
            <RefreshCw size={13} strokeWidth={1.8} />
            {reprocessMut.isPending ? 'Reprocessando…' : 'Reprocessar'}
          </button>
          <button
            type="button"
            onClick={() => setResolveOpen(true)}
            className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 text-[12px] text-ink hover:bg-panel"
          >
            <Wrench size={13} strokeWidth={1.8} />
            Resolver manual
          </button>
          <button
            type="button"
            onClick={() => setIgnoreOpen(true)}
            className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-panel-3 px-2 text-[12px] text-muted hover:bg-panel hover:text-ink"
          >
            <Ban size={13} strokeWidth={1.8} />
            Ignorar
          </button>
        </div>
      )}

      <ResolveManualDialog
        job={job}
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        mut={resolveMut}
      />
      <IgnoreDialog job={job} open={ignoreOpen} onOpenChange={setIgnoreOpen} mut={ignoreMut} />
    </div>
  );
}

interface DialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
}

function DialogShell({ open, onOpenChange, title, children }: DialogShellProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ResolveDialogProps {
  job: FailedProvisioningJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mut: ReturnType<typeof useResolveJobManual>;
}

function ResolveManualDialog({ job, open, onOpenChange, mut }: ResolveDialogProps): JSX.Element {
  // Default coerente com a ação: grant falho → operador costuma confirmar acesso (provisioned);
  // revoke falho → confirmar revogação (revoked).
  const [resolution, setResolution] = useState<'provisioned' | 'revoked'>(
    job.action === 'revoke' ? 'revoked' : 'provisioned',
  );
  const [externalTenantId, setExternalTenantId] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrors({});
    const payload = {
      resolution,
      externalTenantId: externalTenantId.trim() || null,
      notes: notes.trim(),
    };
    const parsed = resolveManualRequestSchema.safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0]?.toString() ?? '_';
        next[path] = issue.message;
      }
      setErrors(next);
      return;
    }
    try {
      await mut.mutateAsync({ jobId: job.id, body: parsed.data });
      toast.success('Job resolvido manualmente.');
      onOpenChange(false);
      setNotes('');
      setExternalTenantId('');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao resolver manualmente.'));
    }
  }

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title="Resolver manualmente">
      <p className="mb-4 text-[12px] leading-relaxed text-muted">
        Use quando você já agiu direto no{' '}
        <strong className="text-ink">{job.saas?.name ?? 'SaaS'}</strong> (criou ou removeu o acesso
        na mão). Isso registra o estado real e fecha o job — não dispara nova chamada ao SaaS.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
        <Field label="Estado real do acesso" required error={errors.resolution}>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={resolution}
              onChange={(e) => setResolution(e.target.value as 'provisioned' | 'revoked')}
            >
              <option value="provisioned">Acesso ativo (provisionado)</option>
              <option value="revoked">Acesso revogado</option>
            </Select>
          )}
        </Field>
        <Field label="External tenant ID (opcional)" error={errors.externalTenantId}>
          {({ inputId }) => (
            <Input
              id={inputId}
              value={externalTenantId}
              onChange={(e) => setExternalTenantId(e.target.value)}
              placeholder="tenant_abc123"
              hasError={Boolean(errors.externalTenantId)}
            />
          )}
        </Field>
        <Field label="O que você fez (mín. 10 caracteres)" required error={errors.notes}>
          {({ inputId }) => (
            <Textarea
              id={inputId}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: criei o tenant manualmente no painel do OwlTask e confirmei o login do cliente."
              hasError={Boolean(errors.notes)}
            />
          )}
        </Field>
        <div className="mt-2 flex items-center justify-end gap-2">
          <PillButton type="button" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
            Cancelar
          </PillButton>
          <PillButton type="submit" solid disabled={mut.isPending}>
            {mut.isPending ? 'Salvando…' : 'Confirmar resolução'}
          </PillButton>
        </div>
      </form>
    </DialogShell>
  );
}

interface IgnoreDialogProps {
  job: FailedProvisioningJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mut: ReturnType<typeof useIgnoreJob>;
}

function IgnoreDialog({ job, open, onOpenChange, mut }: IgnoreDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const parsed = ignoreJobRequestSchema.safeParse({ reason: reason.trim() });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Justificativa inválida.');
      return;
    }
    try {
      await mut.mutateAsync({ jobId: job.id, body: parsed.data });
      toast.success('Job ignorado.');
      onOpenChange(false);
      setReason('');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao ignorar o job.'));
    }
  }

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title="Ignorar falha">
      <p className="mb-4 text-[12px] leading-relaxed text-muted">
        Descarta este job da fila de falhas. Use quando a ação não é mais necessária (ex: cliente já
        cancelou tudo). A justificativa fica no histórico de auditoria.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
        <Field label="Motivo (mín. 10 caracteres)" required error={error ?? undefined}>
          {({ inputId }) => (
            <Textarea
              id={inputId}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: cliente cancelou a assinatura inteira, acesso não precisa mais ser revogado."
              hasError={Boolean(error)}
            />
          )}
        </Field>
        <div className="mt-2 flex items-center justify-end gap-2">
          <PillButton type="button" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
            Cancelar
          </PillButton>
          <PillButton type="submit" solid disabled={mut.isPending}>
            {mut.isPending ? 'Ignorando…' : 'Ignorar job'}
          </PillButton>
        </div>
      </form>
    </DialogShell>
  );
}
